// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@guestsupply/XDL/types";
import color from "@guestsupply/XDL/Utilities/color";
import { styledScrollbar } from "@guestsupply/XDL/Utilities/scroll";
import { TabProps } from "@insite/mobius/Tab";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

const tabSelectedStyle = css`
    outline-width: 0;
    background: transparent;
    color: ${color("common.ui9")};
    box-shadow: inset 4px 0 0 0 ${color("common.ui9")};

    &:hover {
        color: ${color("common.ui8")};
        box-shadow: inset 4px 0 0 0 ${color("primary.main", 0.5)};
    }
`;

const tabStyle = css`
    padding: 4px 32px 4px 12px;
    min-width: initial;
    user-select: none;
    position: relative;
    border: none;
    transition: all 0.2s;
    color: ${color("primary.main", 0.5)};
    box-shadow: inset 2px 0 0 0 ${color("primary.main", 0.5)};
    max-height: 30px;

    &:hover {
        border: none;
        color: ${color("common.ui8")};
        box-shadow: inset 2px 0 0 0 ${color("common.ui8", 0.5)};
        cursor: pointer;
    }
    &:focus {
        ${tabSelectedStyle}
    }
    &:empty {
        display: none;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            size: 18,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        css: css`
            ${tabStyle}
            ${({ selected }: TabProps) => selected && tabSelectedStyle}
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        cssOverrides: {
            tabGroup: css`
                padding: 0;
                position: relative;
                align-items: flex-start;
                ${styledScrollbar(4)}
            `,
            tabContent: css`
                margin-top: 0;
                border: none;
                background: transparent;
                box-shadow: none;
                padding: 20px 0 0;
                @media print {
                    padding: 0;
                    margin-bottom: 12px;
                    display: block;

                    & > ${TypographyStyle} {
                        display: inline-block;
                        ${tabStyle}
                        ${tabSelectedStyle}
                    }

                    & > div {
                        padding: 0;
                        border: 1px solid ${color("common.border")};
                    }
                }
            `,
            wrapper: css`
                width: 100%;
            `,
        },
    },
} as xBaseTheme["tab"];
